import { Component, OnInit, inject, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { BlockComponent } from "@components/container-block/block.component";
import { InputComponent } from "@components/input/input.component";
import { ListColumnDirective } from "@components/list/list-column.directive";
import { ListComponent } from "@components/list/list.component";
import { NgbActiveModal, NgbModal, NgbModalOptions } from "@ng-bootstrap/ng-bootstrap";
import _ from "lodash";
import { BusyService } from "~app/common/components/busy/busy.service";
import { RaskRoller } from "~app/common/models/constants";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { CommandService } from "~app/common/services/cqs/command.service";
import { EventService } from "~app/common/services/cqs/event.service";
import { DirtyFormService, IDirtyFormResult } from "~app/common/services/dirty-form.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { StringUtils } from "~app/common/utils/string-utils";
import { SetDestineringActiveCommand } from "~app/features/transport/transportplanera/models/commands";

import { TplService } from "~app/features/transport/transportplanera/services/tpl.service";
import { ValidationMessageRowComponent } from "../../../../../../../common/validation/validation-message-row/validation-message-row.component";
import { DestineringsraderUpdatedEvent } from "./begransa-virkesorderrader-events";
import { BegransaVirkesorderraderModel, Destinering, TplAoRowsModel } from "./begransa-virkesorderrader-model";

@Component({
    selector: "rask-begransa-virkesorderrader-modal",
    templateUrl: "./begransa-virkesorderrader-modal.component.html",
    styleUrls: ["./begransa-virkesorderrader-modal.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        BlockComponent,
        InputComponent,
        FormsModule,
        ValidationMessageRowComponent,
        ButtonComponent,
        ListComponent,
        ListColumnDirective,
        CheckboxComponent
    ]
})
export class BegransaVirkesorderraderModalComponent implements OnInit {
    readonly activeModal = inject(NgbActiveModal);
    readonly dirtyFormService = inject(DirtyFormService);

    aktivaRader: number;
    antalRader: number;
    arbetsorder: TplAoRowsModel;
    arbetsorderId: number;
    hasBeenSent = false;
    isDirty = false;
    listData: BegransaVirkesorderraderModel[];
    skickadeRader: number = null;
    voStatus: string;

    private readonly authenticationService = inject(AuthenticationService);
    private readonly busyService = inject(BusyService);
    private readonly commandService = inject(CommandService);
    private readonly eventService = inject(EventService);
    private readonly tplService = inject(TplService);

    static open(arbetsorderId: number, backdrop: any, modalService: NgbModal,
        callBack: (result: any) => void = null) {
        const options = {
            keyboard: false,
            backdrop: backdrop,
            centered: true,
            windowClass: "hantera-rader-modal-size"
        } as NgbModalOptions;
        const modalRef = modalService.open(this, options);
        modalRef.componentInstance.arbetsorderId = arbetsorderId;
        if (callBack) {
            modalRef.result.then(r => callBack(r));
        } else {
            modalRef.result.then(r => modalRef.close(r));
        }
    }

    checkChanged(row: BegransaVirkesorderraderModel) {
        if (row.isLocked) {
            return;
        }
        this.isDirty = true;
        this.calculateVoRader();
        this.toggleLockedRows(row);
    }

    close() {
        this.activeModal.close();
    }

    dirtyCheckCallback(result: IDirtyFormResult) {
        if (result.isSkipSave) {
            // close and dismiss
            this.commandService.reset();
            this.close();
        } else if (result.isCancel) {
            // do nothing
        } else {
            // save (Används ej här)
        }
    }

    generateSuggestion() {
        const properties = {
            arbetsorderId: this.arbetsorderId
        };

        const query = this.tplService.getBegransningsForslag(properties);

        this.busyService.addWork(query).subscribe((res: Destinering[]) => {
            this.listData.forEach( x => {
                if (res.some(y => y.id === x.destineringsId)) {
                    x.skickasMedVo = false;
                    this.isDirty = true;
                }
            });
            this.calculateVoRader();
        });
    }

    isModelDirty() {
        return this.isDirty;
    }

    ngOnInit(): void {
        const properties = {
            arbetsorderId: this.arbetsorderId
        };

        const query = this.tplService.getTplArbetsorderDestineringsrader(properties);

        this.busyService.addWork(query).subscribe((res: TplAoRowsModel) => {
            this.arbetsorder = res;
            this.initList();

            this.voStatus = this.hasBeenSent ? "Virkesorder skickad" : "Virkesorder ej skickad";
        });

        this.dirtyFormService.onDirtyNgModel(
            this.isModelDirty.bind(this),
            null,
            d => this.dirtyCheckCallback(d),
            null,
            "Det finns osparade ändringar",
            "Vill du lämna utan att spara?",
            null);
    }

    save() {
        const aktivaRader = this.listData.filter(x => !x.isLocked);

        const inaktivaDestineringar: number[] = [];

        aktivaRader.forEach(x => {
            const command = new SetDestineringActiveCommand(x.destineringsId, x.skickasMedVo);
            this.commandService.addTransactionCommand(command);

            if (!x.skickasMedVo) {
                inaktivaDestineringar.push(x.destineringsId);
            }
        });

        this.busyService
            .addWork(this.commandService.pushCommandQueueTransaction())
            .subscribe((response) => {
                this.isDirty = false;

                const destinerinsraderUpdatedEvent = new DestineringsraderUpdatedEvent(this.arbetsorderId, this.aktivaRader, inaktivaDestineringar);
                this.eventService.emit(destinerinsraderUpdatedEvent);
                this.close();
            });
    }

    tryClose() {
        if (this.dirtyFormService.canClose()) {
            this.close();
        }
    }

    private calculateVoRader() {
        this.antalRader = _.sum(this.listData.map(x => x.nrOfRows)) + this.arbetsorder.antalVilandeVirkesorderrader;
        this.aktivaRader = _.sum(this.listData.filter(x => x.skickasMedVo).map(x => x.nrOfRows)) + this.arbetsorder.antalVilandeVirkesorderrader;
    }

    private getDestinering(destinering: number, ursprung: number) {
        if (destinering > 0) {
            return `${destinering}%`;
        }

        return ursprung === 8 ? "Extra" : "Valbar";
    }

    private initList() {
        this.listData = [];
        this.hasBeenSent = this.arbetsorder.hasBeenSent;
        this.skickadeRader = this.arbetsorder.antalSkickadeVirkesorderrader ?? 0;
        this.arbetsorder.handelssortiment.forEach((hs) => {
            hs.destineringar.forEach((d, i) => {
                this.listData.push({
                    hsId: hs.id,
                    destineringsId: d.id,
                    headerRow: i === 0 ? hs.namn : null,
                    destineringsalternativ: this.writeMottagningsplats(d),
                    transportavstand: d.avstand,
                    destinering: this.getDestinering(d.destinering, d.destineringsursprung),
                    skickasMedVo: d.isActive,
                    isLocked: this.initalSetLockedRow(d, hs.destineringar.length),
                    nrOfRows: d.antalVirkesorderrader ?? 0,
                    hasBeenSent: d.hasBeenSent
                });
            });
        });

        this.calculateVoRader();
    }

    private initalSetLockedRow(dest: Destinering, numberOfDestineringar: number): boolean {
        if ((dest.isActive && dest.destinering > 0) || dest.hasBeenSent) {
            return true;
        }

        if (numberOfDestineringar === 1) {
            return this.authenticationService.hasRole(RaskRoller.role.Arbetsordersystemadmin) ? false : true;
        }

        if (ObjectUtils.hasValue(dest.isLocked)) {
            return dest.isLocked;
        }

        return false;
    }

    private toggleLockedRows(row: BegransaVirkesorderraderModel) {
        if (!row.skickasMedVo && !this.hasBeenSent) {
            const rader = this.listData.filter(x => x.hsId === row.hsId && x.destineringsId !== row.destineringsId && x.skickasMedVo);
            if (rader.length === 1 && !this.authenticationService.hasRole(RaskRoller.role.Arbetsordersystemadmin)) {
                rader[0].isLocked = true;
            }
        }

        if (row.skickasMedVo) {
            const rader = this.listData.filter(x => x.hsId === row.hsId && x.destineringsId !== row.destineringsId && x.skickasMedVo);
            rader.forEach(r => {
                if (!r.destinering.includes("%") && !r.hasBeenSent) {
                    r.isLocked = false;
                }
            });
        }
    }

    private writeMottagningsplats(destinering: Destinering) {
        if (destinering.foradlatHandelssortimentId == null) {
            return destinering.mottagningsplats;
        }

        return StringUtils.suffixString(destinering.mottagningsplats, "//FLIS", 20);
    }
}
